import React, { useCallback, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { fetchSharedFolderWithCards, buildFolderStudyCards } from "@/lib/db";
import { inspectQueryError } from "@/diagnostics";
import { useSafeAsyncData } from "@/hooks/useSafeAsyncData";
import ExplainModal from "@/components/ExplainModal";
import FlashcardView from "@/components/FlashcardView";
import LearnMode from "@/components/LearnMode";
import TestModeView from "@/components/TestModeView";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";

type FolderStudyData = { folderTitle: string; deck: Deck };

type NavState = { deck?: Deck; folderTitle?: string } | null;

export default function SharedFolderStudyPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get("mode") as "learn" | "test" | "games" | null;
  const navigate = useNavigate();
  const { user } = useAuth();
  const publicHomeHref = user ? "/" : "/landing";
  const backHref = token ? `/share/folder/${token}` : publicHomeHref;

  const navState = location.state as NavState;
  const deckFromNav = navState?.deck?.cards?.length ? navState.deck : null;

  const handleFetchError = useCallback((err: unknown) => {
    inspectQueryError({
      queryName: "fetchSharedFolderWithCards",
      flow: "shared.loadFolderStudy",
      table: "folder_shares",
      filters: { share_token: token },
      responseError: err,
    });
  }, [token]);

  const { state, isLoading, hasError } = useSafeAsyncData<FolderStudyData | null>({
    fetch: async () => {
      if (!token) return null;
      if (deckFromNav) {
        return { folderTitle: navState?.folderTitle ?? deckFromNav.title, deck: deckFromNav };
      }
      const result = await fetchSharedFolderWithCards(token);
      if (!result || result.decks.length === 0) return null;
      const cards = buildFolderStudyCards(result.decks);
      if (cards.length === 0) return null;
      const firstDeck = result.decks[0];
      const deck: Deck = {
        id: `folder-${result.folder.id}`,
        title: result.folder.title,
        lang: firstDeck.lang ?? "en",
        emoji: firstDeck.emoji ?? "📁",
        cards,
      };
      return { folderTitle: result.folder.title, deck };
    },
    deps: [token, deckFromNav?.id ?? null],
    emptyIsSuccess: true,
    onError: handleFetchError,
  });

  const loading = isLoading;
  const notFound = !token || (state.status === "success" && state.data == null) || hasError;
  const deck = state.status === "success" && state.data ? state.data.deck : null;
  const folderTitle = state.status === "success" && state.data ? state.data.folderTitle : "";

  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [finished, setFinished] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const cards = deck?.cards ?? [];
  const currentCard = cards[cardIndex];

  const handleNext = (correct: boolean) => {
    setScore((s) => ({
      correct: s.correct + (correct ? 1 : 0),
      incorrect: s.incorrect + (correct ? 0 : 1),
    }));
    if (cardIndex + 1 >= cards.length) {
      setFinished(true);
    } else {
      setCardIndex((i) => i + 1);
      setFlipped(false);
    }
  };

  const handleRestart = () => {
    setCardIndex(0);
    setFlipped(false);
    setScore({ correct: 0, incorrect: 0 });
    setFinished(false);
  };

  const goPrev = () => {
    if (cardIndex > 0) {
      setCardIndex((i) => i - 1);
      setFlipped(false);
    }
  };

  const goNext = () => {
    if (cardIndex + 1 < cards.length) {
      setCardIndex((i) => i + 1);
      setFlipped(false);
    }
  };

  const uiLang = (i18n.language || "en").split("-")[0];
  const scorePillClass =
    "inline-flex min-w-[7rem] items-center justify-center rounded-2xl border px-4 py-3 text-sm font-semibold";

  if (loading) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
        <div style={{ color: "hsl(var(--muted-foreground))", fontFamily: "DM Sans, sans-serif" }}>
          {t("shared.loadingFolder")}
        </div>
      </div>
    );
  }

  if (notFound || !deck) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>
          {t("shared.folderNotFound")}
        </div>
        <div style={{ color: "hsl(var(--muted-foreground))", fontFamily: "DM Sans, sans-serif", marginBottom: 24 }}>
          {t("shared.folderNotFoundHint")}
        </div>
        <Link
          to={publicHomeHref}
          style={{
            padding: "10px 24px",
            borderRadius: 50,
            background: "hsl(var(--primary))",
            color: "white",
            textDecoration: "none",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t("shared.goToLinguaDeck")}
        </Link>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-[100svh] flex-col overflow-x-hidden"
      style={{
        minHeight: "100dvh",
        background: "hsl(var(--bg))",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--bg2))]/95 pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <Link to={backHref} className="flex min-w-0 items-center gap-3 no-underline">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl sm:h-9 sm:w-9"
                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))" }}
              >
                <BookOpen className="h-4 w-4 text-white sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <span className="block truncate text-lg font-bold sm:text-xl" style={{ color: "hsl(var(--foreground))" }}>
                  StudyDo
                </span>
                <div
                  className="truncate text-[10px] italic sm:text-[11px]"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {t("shared.sharedFolderReadOnly")}
                </div>
              </div>
            </Link>

            <div className="hidden items-center gap-3 md:flex">
              <LanguageSwitcher compact />
              <ThemeToggle compact />
            </div>
          </div>

          <div className="flex flex-col gap-2 md:hidden">
            <div className="flex flex-wrap items-center gap-2">
              <LanguageSwitcher compact />
              <ThemeToggle compact />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-4 md:px-6 md:pt-5">
        {finished ? (
          <div
            className="mx-auto w-full max-w-[26rem] rounded-[var(--radius)] border px-6 py-8 text-center sm:px-8 sm:py-10"
            style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{t("shared.sessionComplete")}</div>
            <div style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>
              {t("shared.cardsReviewed", { count: cards.length })}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 28 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent2))" }}>{score.correct}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.correct")}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent3))" }}>{score.incorrect}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.incorrect")}</div>
              </div>
            </div>
            <button
              onClick={handleRestart}
              style={{
                padding: "10px 28px",
                borderRadius: 50,
                border: "none",
                background: "hsl(var(--primary))",
                color: "white",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              🔄 {t("shared.playAgain")}
            </button>
          </div>
        ) : urlMode === "learn" ? (
          <div className="mx-auto w-full max-w-2xl px-3">
            <LearnMode
              cards={cards.filter((c) => (c.front || "").trim() && (c.back || "").trim())}
              deckLang={deck.lang ?? "en"}
              onBack={() => token && navigate(`/share/folder/${token}`)}
              onSessionEnd={() => {}}
            />
          </div>
        ) : urlMode === "test" ? (
          <div className="mx-auto w-full max-w-2xl px-3">
            <TestModeView
              deck={deck}
              onNext={() => {}}
              onMark={() => {}}
            />
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_19rem] xl:items-start xl:gap-5">
            <div className="min-w-0 min-h-0 rounded-[1.75rem] border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] px-2 py-2 shadow-sm sm:px-3 sm:py-3">
              <FlashcardView
                deck={deck}
                cardIndex={cardIndex}
                cardFlipped={flipped}
                isTeacher={false}
                onFlip={() => setFlipped((f) => !f)}
                onMark={handleNext}
                onShuffle={() => {}}
                onShowExplain={() => setShowExplain(true)}
                onPrevCard={goPrev}
                onNextCard={goNext}
                readOnly
              />
              <div className="flex flex-wrap items-center justify-center gap-2 px-2 pb-2 pt-1 text-center sm:gap-3">
                <span
                  className={`${scorePillClass} border-emerald-400/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300`}
                >
                  ✓ {t("shared.correct")}: {score.correct}
                </span>
                <span
                  className={`${scorePillClass} border-rose-400/30 bg-rose-500/10 text-rose-600 dark:text-rose-300`}
                >
                  ✗ {t("shared.incorrect")}: {score.incorrect}
                </span>
              </div>
            </div>

            <div className="min-w-0 flex flex-col gap-3 xl:sticky xl:top-5">
              <div
                className="hidden rounded-[1.5rem] border px-4 py-4 shadow-sm xl:block"
                style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
              >
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
                  {t("shared.sharedFolderReadOnly")}
                </div>
                <div className="mt-2 text-lg font-semibold text-[hsl(var(--foreground))]">{folderTitle}</div>
                <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {t("flashcard.cardCounter", { current: cardIndex + 1, total: cards.length })}
                </div>
                <Link
                  to={backHref}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-white no-underline"
                  style={{
                    background: "hsl(var(--primary))",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  {t("shared.backToFolder")}
                </Link>
              </div>

              <div
                className="rounded-[1.5rem] border px-4 py-4 text-center shadow-sm"
                style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
              >
                <div className="text-sm font-semibold text-[hsl(var(--foreground))]">{t("shared.wantToCreateDecks")}</div>
                <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {t("shared.promoSignUp")}
                </div>
                <Link
                  to="/auth"
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white no-underline"
                  style={{ background: "hsl(var(--primary))" }}
                >
                  {t("shared.getStartedFree")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {showExplain && currentCard && deck && (
        <ExplainModal
          card={currentCard}
          deck={deck}
          uiLang={uiLang}
          onClose={() => setShowExplain(false)}
        />
      )}
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "hsl(var(--bg))",
  fontFamily: "DM Sans, sans-serif",
  textAlign: "center",
  padding: 24,
};
